import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { BiEditAlt } from "react-icons/bi";
import AddBookingCategory from "./addBookingCategory";
import "./Spinner.css";
import "./Loader.css";
import "./hm-theme.css";

function BookingCategories({ selectedOrgcode }) {
  const [categories, setCategories] = useState([]);
  const [company, setCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [loader, setLoader] = useState(true);

  const navigate = useNavigate();

  const getData = async () => {
    setLoader(true);
    const data = JSON.parse(localStorage.getItem("data"));
    const token = data?.token;
    if (!token) return;

    try {
      // Get company details by orgcode to obtain companyId
      const companyRes = await axios.get(
        `/api/companydetails/companycode=${selectedOrgcode}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const companyData = companyRes.data;
      if (!companyData || !companyData.id) {
        throw new Error("Company data not found or invalid.");
      }
      setCompany(companyData);

      // Fetch booking categories for this company
      const catRes = await axios.get(
        `/api/bookingcategory/companyId=${companyData.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCategories(catRes.data || []);
      setLoader(false);
    } catch (error) {
      console.error("Error fetching booking categories:", error);
      setLoader(false);
      handleError(error);
    }
  };

  useEffect(() => {
    if (selectedOrgcode) {
      getData();
    }
  }, [selectedOrgcode]);

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }

  const handleCreateCategory = () => {
    setEditCategoryId(null);
    setIsModalOpen(true);
  };

  const handleEditCategory = (id) => {
    setEditCategoryId(id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    getData();
  };

  return (
    <div>
      {loader && <div className="loading"></div>}

      <div className="hm-card">
        {/* Card Header */}
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">category</i>
            Booking Categories
          </h3>
          <div className="d-flex align-items-center gap-2">
            <button className="btn-hm-primary" onClick={handleCreateCategory}>
              <i className="material-icons" style={{ fontSize: "1rem" }}>
                add
              </i>
              Add Category
            </button>
          </div>
        </div>

        {/* Card Body & Table */}
        <div className="hm-card-body p-0">
          <div style={{ overflowX: "auto" }}>
            <table className="hm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category Name</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="4">
                      <div className="hm-empty-state">
                        <i className="material-icons">category</i>
                        <p>{loader ? "Loading categories..." : "No booking categories found"}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  categories.map((cat, index) => (
                    <tr key={cat.id || index}>
                      <td>{index + 1}</td>
                      <td className="fw-semibold">{cat.name || cat.categoryname}</td>
                      <td className="small text-muted">{cat.description || "—"}</td>
                      <td>
                        <button
                          className="btn btn-link text-dark p-0 border-0"
                          onClick={() => handleEditCategory(cat.id)}
                          title="Edit Category"
                        >
                          <BiEditAlt size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <AddBookingCategory
          isOpen={isModalOpen}
          onRequestClose={handleCloseModal}
          categoryId={editCategoryId}
          companyId={company?.id}
        />
      )}
    </div>
  );
}

export default BookingCategories;
